import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Plus, Sparkles, Link2, MessageSquare, ShieldAlert, ChevronLeft, RotateCcw, CheckCircle2, GitCompare } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Panel, StatusBadge, RiskBadge, BasisBadge, Field } from "@/components/app/ui";
import { ExplainSimply } from "@/components/app/ExplainSimply";
import { RequirementCard } from "@/components/app/RequirementCard";
import { RuleInfo } from "@/components/app/RuleInfo";
import { GuidedNav, ProgressSummary } from "@/components/app/GuidedNav";
import { SectionHelp } from "@/components/app/Help";
import { JourneyBar } from "@/components/app/JourneyBar";
import { api, errMsg, can, fmtDate, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ANSWERS = ["Yes", "Partially", "No", "Not Applicable", "Unknown"];
const FILTERS = ["All", "Needs confirmation", "Flagged changes", "Not Assessed", "Future Effective", "Compliant", "Partially Compliant", "Non-Compliant", "Evidence Required", "Requires Legal Review", "Not Applicable"];
const pendingCarry = (i) => i.carried_forward && !i.carry_confirmed;
const matchFilter = (i, f) => f === "All" || (f === "Needs confirmation" ? pendingCarry(i) : f === "Flagged changes" ? pendingCarry(i) && i.carry_reasons?.length > 0 : i.status === f);

function NewCycleDialog({ open, onOpenChange, current }) {
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [carry, setCarry] = useState(true);
  const [busy, setBusy] = useState(false);
  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/assessments", { name: name || null, carry_forward: carry && !!current });
      toast.success(data.answers_carried ? `New cycle created — ${data.answers_carried} answers carried forward` : "New assessment cycle created");
      nav(`/assessments/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-cycle-dialog">
        <DialogHeader><DialogTitle>New assessment cycle</DialogTitle>
          <DialogDescription>The new cycle snapshots today's regulatory library and applicability. The current cycle becomes read-only.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div><div className="label-caps mb-1">Cycle name</div><Input value={name} onChange={(e) => setName(e.target.value)} placeholder={`DPDP Assessment ${new Date().toISOString().slice(0, 10)}`} data-testid="new-cycle-name-input" /></div>
          {current && (
            <label className="flex items-start gap-3 rounded-md border border-slate-200 p-3">
              <Switch checked={carry} onCheckedChange={setCarry} data-testid="carry-forward-switch" />
              <span className="text-sm"><b>Carry forward previous answers</b>
                <span className="block text-xs text-slate-500">Start from answers, notes and owners in “{current.name}”. Open findings and remediation move to the new cycle. Your team then confirms unchanged answers and updates only what changed.</span></span>
            </label>
          )}
        </div>
        <DialogFooter><Button onClick={create} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="create-cycle-button">{busy ? "Creating…" : "Create cycle"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Assessments() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/assessments").then((r) => setRows(r.data)); }, []);
  return (
    <div>
      <PageHeader title="Assessments" subtitle="Each assessment snapshots the regulatory library version applicable at the time it was created."
        actions={<div className="flex gap-2">{rows.length > 1 && <Link to="/assessments/compare"><Button variant="outline" data-testid="compare-cycles-button"><GitCompare className="mr-1.5 h-4 w-4" />Compare cycles</Button></Link>}
          {can(user, "assess") && <Button onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="new-assessment-button"><Plus className="mr-1.5 h-4 w-4" />New assessment cycle</Button>}</div>} />
      <NewCycleDialog open={open} onOpenChange={setOpen} current={rows.find((r) => r.is_current)} />
      <div className="grid gap-4">
        {rows.map((a) => (
          <Link key={a.id} to={`/assessments/${a.id}`} data-testid={`assessment-card-${a.id}`} className="rounded-lg border border-slate-200 bg-white p-5 transition-colors hover:border-blue-300">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div><div className="font-display font-semibold text-slate-900">{a.name}</div>
                <div className="text-xs text-slate-500">{a.id} · v{a.version} · created {fmtDate(a.created_at)} by {a.created_by} · {Object.entries(a.regulatory_versions).map(([k, v]) => `${k} v${v}`).join(", ")}</div>
                {a.carried_from && <div className="mt-1 flex items-center gap-1 text-xs text-slate-600" data-testid={`carried-summary-${a.id}`}><RotateCcw className="h-3 w-3" />Carried forward from {a.carried_from_name} · {a.carried_total} answers · <b className={a.carried_pending ? "text-amber-700" : "text-emerald-700"}>{a.carried_pending ? `${a.carried_pending} awaiting confirmation` : "all confirmed"}</b></div>}</div>
              {a.is_current ? <span className="rounded-full bg-blue-600 px-2 py-0.5 text-[11px] font-medium text-white">Current</span>
                : a.status === "Superseded" && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600" data-testid={`superseded-${a.id}`}>Superseded · read-only</span>}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">{Object.entries(a.counts).map(([s, n]) => <span key={s} className="flex items-center gap-1"><StatusBadge status={s} /><span className="text-xs text-slate-600">{n}</span></span>)}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}

function CarryNotice({ item, editable, onConfirm, busy }) {
  if (!item.carried_forward) return null;
  if (item.carry_confirmed) return <div className="mb-3 flex items-center gap-1.5 text-xs text-emerald-700" data-testid="carry-confirmed"><CheckCircle2 className="h-3.5 w-3.5" />Carried forward · confirmed by {item.carry_confirmed_by} on {fmtDate(item.carry_confirmed_at)}</div>;
  const flagged = item.carry_reasons?.length > 0;
  return (
    <div className={`mb-4 rounded-md border p-3 text-sm ${flagged ? "border-amber-200 bg-amber-50/60" : "border-blue-200 bg-blue-50/60"}`} data-testid="carry-notice">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 font-medium text-slate-800"><RotateCcw className="h-4 w-4" />Answer carried forward from previous cycle (was: {item.carried_status})</span>
        {editable && <Button size="sm" variant="outline" disabled={busy} onClick={onConfirm} data-testid="confirm-carry-button">Confirm unchanged</Button>}
      </div>
      {flagged ? <ul className="mt-2 list-disc pl-5 text-xs text-amber-800" data-testid="carry-reasons">{item.carry_reasons.map((r) => <li key={r}>{r}</li>)}</ul>
        : <p className="mt-1 text-xs text-slate-500">No changes detected. Confirm if still accurate, or edit and save to update.</p>}
    </div>
  );
}

function ControlDetail({ item, aid, onSaved, evidence, user, readOnly, nav }) {
  const c = item.control;
  const [f, setF] = useState({});
  const [comment, setComment] = useState("");
  const [override, setOverride] = useState("");
  const [reason, setReason] = useState("");
  const [linkId, setLinkId] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF({ answer: item.answer, notes: item.notes, finding: item.finding, owner: item.owner, recommendation: item.recommendation, followup_answers: item.followup_answers || {} }); setComment(""); setOverride(""); setReason(""); }, [item]);
  const editable = can(user, "assess") && !readOnly;
  const showFollow = ["No", "Partially", "Unknown"].includes(f.answer) && c.followups.length > 0;

  const persist = async () => {
    const { data } = await api.put(`/assessments/${aid}/items/${item.id}`, f);
    return data;
  };
  const save = async (extra = {}) => {
    setBusy(true);
    try {
      const body = { ...f, ...extra };
      if (!editable) { delete body.answer; delete body.notes; delete body.finding; delete body.owner; delete body.recommendation; delete body.followup_answers; }
      const { data } = await api.put(`/assessments/${aid}/items/${item.id}`, body);
      onSaved(data);
      toast.success(`Saved — status: ${data.status}`);
      setComment("");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const link = async () => {
    const ev = evidence.find((e) => e.id === linkId);
    try { await api.put(`/evidence/${linkId}`, { control_ids: [...new Set([...ev.control_ids, c.id])] }); toast.success("Evidence linked"); setLinkId(""); onSaved(null); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-5 fade-up" key={item.id} data-testid="control-detail">
      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono text-xs text-slate-500">{c.id}</span><span className="text-xs text-slate-400">·</span><span className="text-xs text-slate-500">{c.domain}</span>
          <BasisBadge basis={c.basis_type} /><StatusBadge status={item.reg_status} testId="detail-reg-status" />
        </div>
        <h2 className="mt-2 font-display text-xl font-bold text-slate-900">{c.name}</h2>
        <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-600">
          <span className="legal-ref" data-testid="detail-legal-ref">{c.regulatory_source} · {c.section_or_rule}</span>
          <span>Effective: <b data-testid="detail-effective-date">{c.effective_from ? fmtDate(c.effective_from) : "Non-statutory"}</b></span>
          <span className="text-slate-400">{c.verification_status}</span>
        </div>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <Field label="Requirement">{c.requirement}</Field>
          <Field label="Why this matters">{c.why_it_matters}</Field>
          <Field label="Applicability">{item.applicability_reason}</Field>
          <Field label="Interpretation notes">{c.interpretation_notes || "—"}</Field>
        </div>
        <div className="mt-4"><RequirementCard control={c} /></div>
      </div>

      <ExplainSimply kind="control" id={c.id} status={item.status} />

      <Panel title="Assessment" testId="assessment-panel" actions={<div className="flex items-center gap-2"><StatusBadge status={item.status} testId="detail-status" /><RiskBadge risk={item.risk} testId="detail-risk" /></div>}>
        <CarryNotice item={item} editable={editable} busy={busy} onConfirm={() => save({ confirm_carry: true })} />
        <div className="text-sm font-medium text-slate-900" data-testid="detail-question">{c.assessment_question}</div>
        <div className="mt-1 text-xs text-slate-500">Expected state: {c.expected_state} · Method: {c.assessment_method}</div>
        <div className="mt-4 flex flex-wrap gap-2">
          {ANSWERS.map((a) => (
            <button key={a} disabled={!editable} onClick={() => setF({ ...f, answer: a })} data-testid={`answer-${a.toLowerCase().replace(/\s+/g, "-")}`}
              className={`rounded-md border px-3.5 py-1.5 text-sm font-medium transition-colors disabled:opacity-60 ${f.answer === a ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-700 hover:border-blue-300"}`}>{a}</button>
          ))}
        </div>
        {showFollow && (
          <div className="mt-4 space-y-3 rounded-md border border-amber-200 bg-amber-50/50 p-4" data-testid="followup-questions">
            <div className="label-caps text-amber-800">Follow-up questions</div>
            {c.followups.map((q, i) => (
              <div key={q}><div className="mb-1 text-sm text-slate-700">{q}</div>
                <Input value={f.followup_answers?.[q] || ""} disabled={!editable} onChange={(e) => setF({ ...f, followup_answers: { ...f.followup_answers, [q]: e.target.value } })} data-testid={`followup-input-${i}`} className="bg-white" /></div>
            ))}
          </div>
        )}
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <div><div className="label-caps mb-1">Current state notes</div><Textarea rows={3} value={f.notes || ""} disabled={!editable} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="detail-notes" /></div>
          <div><div className="label-caps mb-1">Finding (optional override of auto text)</div><Textarea rows={3} value={f.finding || ""} disabled={!editable} onChange={(e) => setF({ ...f, finding: e.target.value })} data-testid="detail-finding" /></div>
          <div><div className="label-caps mb-1">Owner</div><Input value={f.owner || ""} disabled={!editable} onChange={(e) => setF({ ...f, owner: e.target.value })} data-testid="detail-owner" /></div>
          <div><div className="label-caps mb-1">Recommendation</div><Input value={f.recommendation || ""} disabled={!editable} onChange={(e) => setF({ ...f, recommendation: e.target.value })} data-testid="detail-recommendation" /></div>
        </div>
        <div className="mt-4 rounded-md bg-slate-50 p-3 text-xs text-slate-600" data-testid="risk-drivers">
          <b>Risk: {item.risk}</b> · Confidence: {item.confidence}
          <ul className="mt-1 list-disc pl-5">{item.risk_drivers?.map((d) => <li key={d}>{d}</li>)}</ul>
          {item.status === "Evidence Required" && <div className="mt-2 text-indigo-700">Insufficient evidence to determine control effectiveness.</div>}
          {item.reg_status === "Future Effective" && <div className="mt-1 text-purple-700">Future-effective requirement — assessed for readiness.</div>}
        </div>
        {editable && <Button onClick={() => save()} disabled={busy} variant="outline" className="mt-4" data-testid="save-assessment-button">{busy ? "Saving…" : "Save response"}</Button>}
      </Panel>

      <Panel title="Evidence" testId="evidence-panel">
        <div className="label-caps mb-1">Evidence required</div>
        <ul className="mb-3 list-disc pl-5 text-sm text-slate-700">{c.evidence_required.map((e) => <li key={e}>{e}</li>)}</ul>
        <div className="label-caps mb-1">Evidence linked</div>
        {item.evidence?.length ? item.evidence.map((e) => (
          <div key={e.id} data-testid={`linked-evidence-${e.id}`} className="flex items-center justify-between border-b py-1.5 text-sm"><span>{e.name} <span className="text-xs text-slate-400">v{e.version}</span></span><StatusBadge status={e.verification_status} /></div>
        )) : <p className="text-sm text-slate-400">No evidence linked.</p>}
        {can(user, "evidence") && (
          <div className="mt-3 flex gap-2">
            <Select value={linkId || undefined} onValueChange={setLinkId}>
              <SelectTrigger className="max-w-sm" data-testid="link-evidence-select"><SelectValue placeholder="Link existing evidence…" /></SelectTrigger>
              <SelectContent>{evidence.filter((e) => !e.control_ids.includes(c.id)).map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button variant="outline" disabled={!linkId} onClick={link} data-testid="link-evidence-button"><Link2 className="mr-1.5 h-4 w-4" />Link</Button>
            <Link to="/evidence"><Button variant="ghost" data-testid="goto-upload-evidence">Upload new</Button></Link>
          </div>
        )}
      </Panel>

      <Panel title="AI observations" testId="ai-observations-panel" actions={<Sparkles className="h-4 w-4 text-blue-600" />}>
        {item.ai_observations?.length ? item.ai_observations.map((o, i) => (
          <div key={i} className={`mb-2 rounded-md border p-3 text-sm ${o.type === "gap" ? "border-amber-200 bg-amber-50/60" : "border-slate-200"}`} data-testid={`ai-observation-${i}`}>
            <div className="flex items-center justify-between text-xs"><b>{o.type === "gap" ? "Potential gap" : "Potential evidence"} · {o.evidence}</b><span>Confidence: {o.confidence}</span></div>
            <div className="mt-1 text-slate-700">{o.gap || o.evidence_found}</div>
            {o.quote && <blockquote className="mt-1 border-l-2 border-blue-300 pl-2 text-xs italic text-slate-600">"{o.quote}" — {o.location} {o.quote_verified ? "✓ verified in source" : "(unverified)"}</blockquote>}
          </div>
        )) : <p className="text-sm text-slate-400">No AI observations yet. Run AI analysis on linked evidence from the Evidence page.</p>}
        <p className="mt-2 flex items-center gap-1 text-xs text-sky-700"><ShieldAlert className="h-3.5 w-3.5" />Potential issue identified — human validation required.</p>
      </Panel>

      <Panel title="Reviewer comments & status override" testId="reviewer-panel" actions={<MessageSquare className="h-4 w-4 text-slate-400" />}>
        {item.reviewer_comments?.map((r, i) => <div key={i} className="mb-2 rounded bg-slate-50 p-2 text-sm"><div className="text-xs text-slate-500">{r.by} ({r.role}) · {fmtDateTime(r.at)}</div>{r.text}</div>)}
        {(can(user, "assess") || can(user, "finding")) && !readOnly && (
          <div className="flex gap-2"><Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Add reviewer comment…" data-testid="reviewer-comment-input" />
            <Button variant="outline" disabled={!comment} onClick={() => save({ reviewer_comment: comment })} data-testid="add-comment-button">Add</Button></div>
        )}
        {editable && (
          <div className="mt-4 grid gap-2 md:grid-cols-[200px_1fr_auto]">
            <Select value={override || undefined} onValueChange={setOverride}>
              <SelectTrigger data-testid="override-select"><SelectValue placeholder="Override status…" /></SelectTrigger>
              <SelectContent>{["Compliant", "Partially Compliant", "Non-Compliant", "Evidence Required", "Requires Legal Review", "Not Applicable"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required, audited)" data-testid="override-reason-input" />
            <Button variant="outline" disabled={!override || !reason} onClick={() => save({ status_override: override, reason })} data-testid="apply-override-button">Apply</Button>
            {item.status_override && <Button variant="ghost" size="sm" onClick={() => save({ clear_override: true, reason: "Override cleared" })} data-testid="clear-override-button">Clear override ({item.status_override})</Button>}
          </div>
        )}
      </Panel>
      {item.na_justification && <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600" data-testid="na-justification-display"><b>Not applicable justification</b> ({item.na_by}): {item.na_justification}</div>}
      <GuidedNav item={item} aid={aid} editable={editable} persist={persist} onDone={onSaved} {...nav} />
    </div>
  );
}

export function AssessmentWorkspace() {
  const { id } = useParams();
  const { user } = useAuth();
  const [a, setA] = useState(null);
  const [sel, setSel] = useState(null);
  const [filter, setFilter] = useState("All");
  const [evidence, setEvidence] = useState([]);
  const [prog, setProg] = useState(null);
  const loadProg = () => api.get(`/assessments/${id}/progress`).then((r) => setProg(r.data));
  const load = () => { api.get(`/assessments/${id}`).then((r) => setA(r.data)); api.get("/evidence").then((r) => setEvidence(r.data)); loadProg(); };
  useEffect(load, [id]);
  const items = useMemo(() => (a?.items || []).filter((i) => i.applicable && matchFilter(i, filter)), [a, filter]);
  const groups = useMemo(() => items.reduce((g, i) => ((g[i.control.domain] = g[i.control.domain] || []).push(i), g), {}), [items]);
  useEffect(() => { if (a && prog && !sel && items.length) setSel(items.find((i) => i.id === prog.last_item_id)?.id || items[0].id); }, [a, prog, items, sel]);
  useEffect(() => { if (sel) api.post(`/assessments/${id}/position`, { item_id: sel }).catch(() => {}); }, [sel, id]);
  if (!a) return <div className="text-sm text-slate-400">Loading assessment…</div>;
  const ordered = Object.values(groups).flat();
  const idx = ordered.findIndex((i) => i.id === sel);
  const go = (d) => { const n = ordered[idx + d]; if (n) { setSel(n.id); window.scrollTo({ top: 0, behavior: "smooth" }); } };
  const navProps = { hasPrev: idx > 0, hasNext: idx >= 0 && idx < ordered.length - 1, onPrev: () => go(-1), onNext: () => go(1) };
  const current = a.items.find((i) => i.id === sel);
  const readOnly = a.status === "Superseded";
  const carried = a.items.filter((i) => i.applicable && i.carried_forward);
  const pending = carried.filter(pendingCarry);
  const unchanged = pending.filter((i) => !i.carry_reasons?.length);
  const onSaved = (data) => {
    if (data) setA((x) => ({ ...x, items: x.items.map((i) => (i.id === data.id ? { ...i, ...data } : i)) }));
    loadProg();
  };
  const confirmAll = async () => {
    try { const { data } = await api.post(`/assessments/${a.id}/confirm-carried`); toast.success(`${data.confirmed} unchanged answers confirmed`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <Link to="/assessments" className="mb-2 inline-flex items-center text-xs text-slate-500 hover:text-blue-600"><ChevronLeft className="h-3 w-3" />Assessments</Link>
      <PageHeader title={a.name} subtitle={`${a.id} · version ${a.version} · regulatory library ${a.library_version}`} />
      <JourneyBar />
      <SectionHelp id="assessment" />
      <ProgressSummary p={prog} />
      {readOnly && <div className="mb-4 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600" data-testid="superseded-banner">This cycle has been superseded by a newer assessment and is read-only.</div>}
      {a.carried_from && carried.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border border-blue-200 bg-blue-50/60 p-3 text-sm" data-testid="carry-banner">
          <span className="flex items-center gap-2 text-slate-700"><RotateCcw className="h-4 w-4 text-blue-600" />
            {carried.length} answers carried forward from <b>{a.carried_from_name}</b> · <b data-testid="carry-pending-count">{pending.length}</b> awaiting confirmation{pending.length > unchanged.length && <> · <b className="text-amber-700">{pending.length - unchanged.length} flagged for changes</b></>}</span>
          <div className="flex gap-2">
            {pending.length > 0 && <Button size="sm" variant="ghost" onClick={() => { setFilter("Needs confirmation"); setSel(null); }} data-testid="review-carried-button">Review pending</Button>}
            {can(user, "assess") && !readOnly && unchanged.length > 0 && <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={confirmAll} data-testid="confirm-all-carried-button">Confirm {unchanged.length} unchanged</Button>}
          </div>
        </div>
      )}
      <div className="grid gap-6 lg:grid-cols-[340px_1fr]">
        <div className="rounded-lg border border-slate-200 bg-white lg:sticky lg:top-0 lg:max-h-[calc(100vh-180px)] lg:overflow-y-auto" data-testid="control-list">
          <div className="border-b p-3">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger data-testid="control-filter-select"><SelectValue /></SelectTrigger>
              <SelectContent>{FILTERS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {Object.entries(groups).map(([d, list]) => (
            <div key={d}>
              <div className="bg-slate-50 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{d}</div>
              {list.map((i) => (
                <button key={i.id} onClick={() => setSel(i.id)} data-testid={`control-item-${i.control_id}`}
                  className={`flex w-full items-start justify-between gap-2 border-l-4 px-3 py-2 text-left text-sm transition-colors ${sel === i.id ? "border-blue-600 bg-blue-50" : "border-transparent hover:bg-slate-50"}`}>
                  <span className="min-w-0"><span className="flex items-center gap-1 font-mono text-[10px] text-slate-400">{i.control_id}
                    {pendingCarry(i) && <RotateCcw className={`h-3 w-3 ${i.carry_reasons?.length ? "text-amber-600" : "text-blue-500"}`} data-testid={`carry-indicator-${i.control_id}`} />}</span>
                    <span className="block truncate text-slate-800">{i.control.name} <RuleInfo c={i.control} /></span>{prog?.states?.[i.id] && prog.states[i.id] !== "Completed" && <span className="text-[10px] text-slate-400" data-testid={`progress-state-${i.control_id}`}>{prog.states[i.id]}</span>}</span>
                  <StatusBadge status={i.status} />
                </button>
              ))}
            </div>
          ))}
          {!items.length && <p className="p-6 text-center text-sm text-slate-400">No controls match.</p>}
        </div>
        {current ? <ControlDetail item={current} aid={a.id} onSaved={onSaved} evidence={evidence} user={user} readOnly={readOnly} nav={navProps} /> : <div />}
      </div>
    </div>
  );
}
